import { Fragment, type ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Button } from '../../Button/Button';

export interface WithholdingFormDialogProps {
  open: boolean;
  title: string;
  /** Cuerpo del formulario (campos). */
  children: ReactNode;
  submitLabel: string;
  cancelLabel: string;
  /** Mutación en vuelo: bloquea botones y muestra spinner en confirmar. */
  isSubmitting: boolean;
  /** Deshabilita el botón de enviar (p.ej. formulario inválido). */
  submitDisabled?: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

/**
 * Shell de diálogo con formulario para el panel de retenciones (Agregar/Modificar).
 *
 * Construido sobre el `Dialog` de Headless UI (mismo cimiento que `AlertDialog`),
 * porque `AlertDialog` no admite contenido de formulario (`children`). El submit
 * se dispara desde el `<form>` (Enter) y desde el botón principal.
 */
export function WithholdingFormDialog({
  open,
  title,
  children,
  submitLabel,
  cancelLabel,
  isSubmitting,
  submitDisabled = false,
  onSubmit,
  onClose,
}: WithholdingFormDialogProps) {
  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" open={open} className="relative z-50 text-left" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-md bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl transform overflow-hidden text-left align-middle shadow-xl transition-all">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!submitDisabled && !isSubmitting) onSubmit();
                  }}
                  className="p-6 flex flex-col gap-4"
                >
                  <Dialog.Title
                    as="p"
                    className="text-sm leading-5 font-bold text-content-primary dark:text-dark-content-primary"
                  >
                    {title}
                  </Dialog.Title>

                  <div className="flex flex-col gap-3">{children}</div>

                  <div className="flex flex-wrap items-center justify-end gap-3 mt-2">
                    <Button type="plain" size="base" onClick={handleClose} disabled={isSubmitting}>
                      {cancelLabel}
                    </Button>
                    <Button
                      type="default"
                      size="base"
                      htmlType="submit"
                      disabled={isSubmitting || submitDisabled}
                    >
                      <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                        {submitLabel}
                        {isSubmitting && (
                          <svg
                            className="animate-spin h-4 w-4 text-white shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        )}
                      </span>
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
